import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useAppStore } from '@/stores/app'

const APP_NAME = 'XGuard - Crossing Guards'

/**
 * Names the page in the browser tab, the history and the announcement a screen
 * reader makes on arriving somewhere new. A single-page application does not
 * retitle itself; without this every route shares one title.
 */
export function setDocumentTitle(title?: string) {
  document.title = title ? `${title} - ${APP_NAME}` : APP_NAME
}

const routes: RouteRecordRaw[] = [
  { path: '/', name: 'home', component: () => import('@/views/Home.vue'), meta: { requiresAuth: true, title: 'Home' } },
  { path: '/login', name: 'login', component: () => import('@/views/Login.vue'), meta: { requiresAuth: false, title: 'Sign in' } },
  { path: '/viewtimeday', name: 'viewtimeday', component: () => import('@/views/ViewTimeDay.vue'), meta: { requiresAuth: true, title: 'Enter Time' } },
  { path: '/entrytime', name: 'entrytime', component: () => import('@/views/EntryTime.vue'), meta: { requiresAuth: true, title: 'New Time Entry' } },
  { path: '/viewtime', name: 'viewtime', component: () => import('@/views/ViewTime.vue'), meta: { requiresAuth: true, title: 'View Time' } },
  { path: '/callout', name: 'callout', component: () => import('@/views/CallOut.vue'), meta: { requiresAuth: true, title: 'Call Out' } },
  { path: '/profile', name: 'profile', component: () => import('@/views/Profile.vue'), meta: { requiresAuth: true, title: 'Profile' } },
  { path: '/about', name: 'about', component: () => import('@/views/About.vue'), meta: { requiresAuth: false, title: 'About' } },
  // Log out as a route, so the phone menu (links only, no click handlers) can
  // offer it. Same as the account menu's Log out: a redirect to Microsoft that
  // comes back to the login page, so this route never renders.
  {
    path: '/logout',
    name: 'logout',
    component: () => import('@/views/Login.vue'),
    meta: { requiresAuth: false, title: 'Log out' },
    beforeEnter: async () => {
      useAppStore().reset()
      await useAuthStore().logOut()
      return false
    },
  },
  { path: '/authfailed', name: 'authfailed', component: () => import('@/views/AuthFailed.vue'), meta: { requiresAuth: false, title: 'Sign-in problem' } },
  // Help center: every topic renders the same Help.vue (content is chosen by
  // path). The overview keeps the name 'help' since the top nav links to it, and
  // the footer links to the policy topics by name.
  //
  // Public, like the template's: the footer shows on the login screen too, and
  // a Terms of Use link that bounces you to a sign-in page is no use to the
  // person reading it.
  ...[
    '/help',
    '/help/getting-started',
    '/help/getting-started/signing-in',
    '/help/getting-started/access',
    '/help/home',
    '/help/time',
    '/help/time/day',
    '/help/time/entry',
    '/help/time/delete',
    '/help/time/pay-period',
    '/help/time/deadline',
    '/help/profile',
    '/help/requests',
    '/help/faq',
    '/help/contact',
    '/help/policies',
    '/help/terms-of-use',
    '/help/privacy',
    '/help/accessibility',
  ].map((path) => ({
    path,
    name: path === '/help' ? 'help' : 'help-' + path.slice('/help/'.length).replace(/\//g, '-'),
    component: () => import('@/views/Help.vue'),
    meta: { requiresAuth: false, title: 'Help Center' },
  })),
  // vue-router 4 replaces the '*' catch-all with a named parameter + regex.
  { path: '/:pathMatch(.*)*', name: 'notfound', component: () => import('@/views/NotFound.vue'), meta: { requiresAuth: false, title: 'Page not found' } },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

router.beforeEach(async (to) => {
  if (!to.matched.some((record) => record.meta.requiresAuth)) return true

  const token = await useAuthStore().getToken()
  if (!token) return { name: 'login' }

  return true
})

router.afterEach((to) => {
  setDocumentTitle(to.meta.title as string | undefined)
})

export default router
